import { Stack, useTheme } from "@mui/material";
import generatePDF from "react-to-pdf";
import moment from "moment";
import { useTranslation } from "react-i18next";
import CustomButton from "../components/CustomButton";
import { useEffect, useRef, useState } from "react";
import { apiUrl } from "../config/settings";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setShowLoading } from "../store/sysSlice";
import NullCheck from "../components/NullCheck";
import { DATE_TIME_FORMAT, PMT_TYPE_ID } from "../config/const";
import { findKeyByValue, listTolineText } from "../config/functions";
import { useReactToPrint } from "react-to-print";

const ServicesSummary = ({ callId, bizData }) => {
  const summaryRef = useRef(null);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { bizPath, guid } = useParams();
  const { t } = useTranslation();
  const [biz, setBiz] = useState(null);
  const [data, setData] = useState(null);
  const [isOutputMode, setIsOutputMode] = useState(false);

  const clientDetailsStyle = {
    textAlign: "center",
  };

  const title = {
    margin: 0,
    fontSize: 14,
    fontWeight: "bold",
  };

  const pStyle = {
    margin: 0,
  };

  const options = {
    filename: `${data?.callId}.pdf`,
    page: { margin: 0 },
  };

  const getTargetElement = () => document.getElementById("summary");

  useEffect(() => {
    if (bizPath && guid) {
      getBizData();
      getServices();
    }
  }, [bizPath, guid]);

  useEffect(() => {
    if (callId) getServices(callId);
    if (bizData) setBiz(bizData);
  }, [callId, bizData]);

  const getBizData = () => {
    if (bizPath) {
      dispatch(setShowLoading(true));
      axios.get(`${apiUrl}/miniSite/biz/${bizPath}`)
        .then((response) => {
          if (response.status === 200) {
            setBiz(response?.data);
          }
          dispatch(setShowLoading(false));
        })
        .catch((error) => {
          console.error("Auth error");
          dispatch(setShowLoading(false));
        });
    }
  };

  const getServices = (callId) => {
    const id = callId || guid;
    if (id) {
      dispatch(setShowLoading(true));
      axios
        .get(`${apiUrl}/servicesCall/servicesSummary/${id}`)
        .then((res) => {
          dispatch(setShowLoading(false));
          setData(res?.data?.data);
        })
        .catch((error) => {
          console.log("error", error);
        });
    }
  };

  const downloadPdf = async () => {
    try {
      setIsOutputMode(true);
      await new Promise((r) => setTimeout(r, 50));
      await generatePDF(getTargetElement, {
        ...options,
        page: { margin: 0, format: "a4", orientation: "portrait" },
      });
    } finally {
      setIsOutputMode(false);
    }
  };

  const printFrame = useReactToPrint({
    content: () => summaryRef.current,
    pageStyle: `
      @page { size: A4; margin: 0; }
      html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* ביטוח כפול ש-#summary באמת בגודל A4 בזמן הדפסה */
      #summary { width: 209.8mm !important; min-height: 297mm !important; box-sizing: border-box; }
    `,
    onBeforeGetContent: () => setIsOutputMode(true),
    onAfterPrint: () => setIsOutputMode(false),
  });

  if (!biz || !data) return null;

  return (
    <Stack height="100%" spacing={1}>
      <div
        ref={summaryRef}
        id="summary"
        style={{
          backgroundColor: "#fff",
          width: isOutputMode ? "209mm" : "auto",
          minHeight: isOutputMode ? "292mm" : "94%",
          boxSizing: "border-box",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 2,
          direction: "rtl",
          display: "flex",
          overflowX: "hidden",
          overflowY: "auto",
          border: isOutputMode ? "0px" : "1px solid #000",
        }}
      >
        <div style={{ width: "100%" }}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 10,
              backgroundColor: theme.palette.primary.main,
            }}
          >
            <span
              style={{
                fontSize: 16,
                fontWeight: "bold",
                color: theme.palette.primary.contrastText,
              }}
            >
              {`${t("SERVICES_CALL_AND_ORDER")}: ${data?.callId}`}
            </span>
          </div>

          {/* Biz Details */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              padding: 5,
              paddingLeft: isOutputMode ? 15 : 10,
              paddingRight: isOutputMode ? 15 : 10,
            }}
          >
            <span style={{ fontSize: 16 }}>{`${biz?.bizName}`}</span>
            <span style={{ fontSize: 14 }}>{`${t("TEL")}: ${biz.bizTel}`}</span>

            <NullCheck valueForCheck={biz.bizAddr}>
              <span style={{ fontSize: 14 }}>{`${t("ADDR")}: ${biz.bizAddr}`}</span>
            </NullCheck>

            <span style={{ fontSize: 14 }}>{`${t("EMAIL")}: ${biz.bizEmail}`}</span>
          </div>

          {/* content */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: 5,
              paddingLeft: isOutputMode ? 15 : 10,
              paddingRight: isOutputMode ? 15 : 10,
            }}>
            {/* client Details */}
            <div style={{ border: "1px solid black", borderRadius: "10px", overflow: "hidden" }}>
              <table width="100%" border="1" style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={clientDetailsStyle}>
                      <p style={title}>קריאת לקוח</p>
                      <p style={pStyle}>{data?.clientCallId ?? "-"}</p>
                    </td>
                    <td style={clientDetailsStyle}>
                      <p style={title}>חברה</p>
                      <p style={pStyle}>{data.clienCompany ?? "-"}</p>
                    </td>
                    <td style={clientDetailsStyle}>
                      <p style={title}>איש קשר</p>
                      <p style={pStyle}>{data.name ?? "-"}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* call Details */}
            <div style={{ border: "1px solid black", borderRadius: "10px", overflow: "hidden" }}>
              <table width="100%" border="1" style={{ borderCollapse: "collapse" }}>
                <tbody>
                  <tr>
                    <td style={clientDetailsStyle}>
                      <p style={title}>תאריך עבודה</p>
                      <p style={pStyle}>
                        {data.workDt ? moment(data.workDt).format(DATE_TIME_FORMAT) : "."}
                      </p>
                    </td>
                    <td style={clientDetailsStyle}>
                      <p style={title}>עובד</p>
                      <p style={pStyle}>{data.employeeName ?? "."}</p>
                    </td>
                    <td style={clientDetailsStyle}>
                      <p style={title}>סטטוס תשלום</p>
                      <p style={pStyle}>
                        {t(findKeyByValue(PMT_TYPE_ID, data.pmtTypeId)) ?? "."}
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div
              style={{
                minHeight: 100,
                padding: 5,
                borderRadius: 10,
                border: `1px solid ${theme.palette.primary.main}`,
              }}
            >
              {listTolineText(data?.contentWithItems)}
            </div>

            <NullCheck valueForCheck={data?.jobDoneDesc || data?.works?.length > 0}>
              <div
                className="display-linebreak"
                style={{
                  minHeight: 100,
                  padding: 5,
                  borderRadius: 10,
                  gap: 3,
                  border: `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <p style={{ margin: 0, fontWeight: "bold" }}>בוצע:</p>
                {data?.jobDoneDesc ? (
                  <div
                    style={{
                      marginTop: 5,
                      padding: 5,
                      borderRadius: 10,
                      border: "0.5px solid #000",
                    }}
                  >
                    {data?.jobDoneDesc}
                  </div>
                ) : null}

                {data?.works?.map((work, index) => (
                  <div
                    key={index}
                    style={{
                      marginTop: 10,
                      padding: 5,
                      borderRadius: 10,
                      border: "0.5px solid #000",
                    }}
                  >
                    <p style={{ margin: 0 }}>{`${work.desc}`}</p>
                  </div>
                ))}
              </div>
            </NullCheck>

            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "end",
                gap: 5,
              }}
            >
              <span>{t("סכום לתשלום")}:</span>
              <div
                style={{
                  width: 150,
                  borderRadius: 10,
                  border: `1px solid ${theme.palette.primary.main}`,
                }}
              >
                <p style={{ margin: 0, textAlign: "center", fontSize: 16 }}>
                  {data.pmtTypeId === 1 ? `₪${data.price}` : ""}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/** footer */}
        <div
          style={{
            textAlign: "center",
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          }}
        >
          <p style={{ margin: 5, fontWeight: "bold" }}> נוצר ע"י מערכת MyBee</p>
        </div>
      </div>

      <Stack
        gap={1}
        width="100%"
        spacing={1}
        bottom={0}
        zIndex={999}
        direction="row"
        justifyContent="space-between"
      >
        <CustomButton fullWidth title="DOWNLOAD" onClick={downloadPdf} />
        <CustomButton fullWidth title="PRINT" onClick={printFrame} />
      </Stack>
    </Stack>
  );
};
export default ServicesSummary;
